import classNames from 'classnames';
import PropTypes from 'prop-types';
import { noop, isArray, union } from 'underscore';
import React, { useContext, useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { withBannerService, CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { getGenericImageObjectAsync } from '@bingads-webui-campaign/image-experience-utils';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
} from '@bingads-webui-campaign-react/form-error';
import { AssetScenario } from '@bingads-webui-react/copilot-event-provider';
import { AssetEventFeature } from '@bingads-webui-campaign-react/copilot-common';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withScope } from '@bingads-webui-react/scope-service';
import { useFormService } from '@bingads-webui-react/form';
import { useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { legacyI18nOfListingGroup } from '@bingads-webui-campaign-react/listing-groups-page';
import { StepHeader } from '@bingads-webui-campaign-react/pmax-common';
import { SystemErrors, AssetGroupValidationGroup, FLOWS, AppAdsPreview } from '@bingads-webui-campaign-react/unified-app-shared';
import {
  ValidatedImageExperienceWrapper,
  PerfMaxResponsiveAdPreviewUAPL
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';

import { getAssetStepStyles } from '../use-assets-step-styles';

import { AssetGroupNameSection } from './asset-group-name-section';
import { AssetsSection } from './assets-section';
import { adPreviewConfig, unifiedAppAdsSurfaces } from '../model/constants';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);
const MaxImageCount = 1;

const AssetsStepFormInternal = ({
  data,
  onDataChange: onDataChangeProp,
  error,
  scope,
  assetPanels,
}) => {
  const isMounted = useIsMounted();

  const { state } = useWizardService();

  const {
    wizardData: { flow },
  } = state.context;

  const permissions = useContext(PermissionsContext);

  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);
  const [recommendationContext] = useContext(RecommendationContext);

  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);
  const transparentLogoContainerRef = useRef(null);
  const ImageSPOTLIGHTContainerRef = useRef(null);
  const ImagePRONGContainerRef = useRef(null);
  const portraitContainerRef = useRef(null);

  const stylingProps = useMemo(() =>
    ({ component: { placements: 3 } }), []);

  const getStyles = useResponsiveStyles(getAssetStepStyles, stylingProps);

  const { cid, aid } = scope.values || {};

  const [selectedVideoId, setSelectedVideoId] = useState(0);

  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [ImageSPOTLIGHTExperiencePortalContainerState, setImageSPOTLIGHTExperiencePortalContainerState] = useState({});
  const [ImagePRONGExperiencePortalContainerState, setImagePRONGExperiencePortalContainerState] = useState({});

  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [prongImageExperienceAddNewImageCallback, setProngImageExperienceAddNewImageCallback] = useState(noop);
  const [imageSpotlightExperienceAddNewImageCallback, setImageSpotlightExperienceAddNewImageCallback] = useState(noop);

  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [transparentLogoExperiencePortalContainerState, setTransparentLogoExperiencePortalContainerState] = useState({});
  const [previewTransparentLogoSharedState, setPreviewTransparentLogoSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [previewImageSPOTLIGHTSharedState, setPreviewImageSPOTLIGHTSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [previewImagePRONGSharedState, setPreviewImagePRONGSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [appLogo, setAppLogo] = useState(null);

  const {
    assetGroup,
    campaign,
  } = data || {};

  const {
    AppSetting,
  } = campaign || {};

  const {
    Images,
    CallToAction,
    LongHeadlines,
    Descriptions,
    Headlines,
    videos = [],
    ImageSPOTLIGHT,
    ImagePRONG,
    TransparentLogo,
  } = assetGroup || {};

  const onDataChange = useCallback((result) => {
    if (!isMounted.current) return;
    onDataChangeProp({ ...result });
  }, [isMounted, onDataChangeProp]);

  const onReset = useCallback(() => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          Images: {
            ...assetGroup.Images,
            ImageList: [],
            ImageAssets: null,
          },
          Headlines: {
            Headline0: '',
            Headline1: '',
            Headline2: '',
          },
          LongHeadlines: {
            LongHeadline0: '',
          },
          Descriptions: {
            Description0: '',
            Description1: '',
          },
        },
      }),
      isValid: false,
    });
  }, [assetGroup, onDataChange]);

  const onUpdateImageList = useCallback((newImageList) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        assetGroup: {
          ...prevData.assetGroup,
          Images: {
            ...prevData.assetGroup.Images,
            ImageList: newImageList,
            ImageAssets: null,
          },
        },
      }),
    });
  }, [onDataChange]);

  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyGlobalI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap, legacyI18nOfListingGroup]), [i18n]);

  const mergedI18n = useMemo(() => {
    const mergedTempI18n = mergeI18n(
      legacyI18n,
      i18n
    );

    return mergeI18n(
      legacyGlobalI18n,
      mergedTempI18n
    );
  }, [i18n, legacyI18n, legacyGlobalI18n]);

  const { state: { context: { saveFailed } } } = useFormService();

  const isSaving = matchStepState(state, STEP_STATES.SAVING);
  const isCanceling = matchStepState(state, STEP_STATES.CANCELING);

  const perfMarker = useContext(PerformanceMarkerContext);

  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({
    previewImageId: null,
    previewLogoId: null,
    cropImageObj: null,
    editingPlacementType: '',
  });

  const { postAssetEventLog } = recommendationContext || noop;

  const maxUploadNum = useMemo(() => {
    const imageListLength = Images && Images.ImageList && isArray(Images.ImageList) ? Images.ImageList.length : 0;
    return 20 - imageListLength;
  }, [Images]);

  const finalUrlFromApp = useMemo(() => {
    if (AppSetting) {
      if (flow === FLOWS.WINDOWS) {
        return `https://apps.microsoft.com/detail/${AppSetting.AppId}`;
      }
      return AppSetting.AppUrl;
    }
    return null;
  }, [AppSetting, flow]);

  const {
    copilotBanner,
    suggestionPoller,
  } = useCopilot({
    maxUploadNum,
    addNewShortHeadlinesCallback,
    addNewLongHeadlinesCallback: noop,
    addNewDescriptionCallback,
    imageExperienceAddNewImageCallback,
    // newCTACallback,
    currentCTA: CallToAction,
    onReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data: {
      ...assetGroup,
      finalUrls: [finalUrlFromApp],
      FinalUrlForRecommendation: finalUrlFromApp,
    },
    isSaving,
    isCanceling,
    enableCopilotRecommendations: true,
    feature: AssetEventFeature.PMAX, // TODO Feature name
    assetScenario: AssetScenario.CREATE_WORK_FLOW,
  });

  const imageExperienceComponents = useMemo(() => (
    <div>
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        imagesContaineRef={imagesContainerRef}
        logosContaineRef={logosContainerRef}
        updateFinalUrlCallback={() => {}} // TODO
        isLogoEnabled={false}
        previewImagesSharedState={previewImagesSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        updatePortalContainerState={setImageExperiencePortalContainerState}
        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
        validationProps={{ data: assetGroup, initialValue: assetGroup }}
        suggestionPoller={suggestionPoller}
        wizardPerfMarker={perfMarker}
        postAssetEventLog={postAssetEventLog}
        UAPLPMaxIntegration
        showCopilotOptionForImage={permissions.IsBackgroundSceneEnabled}
        // currently there is implementation in image experience to unify prong and spotlight images for windows store.
        // but we will not use it for now. We will keep two separate panels for prong and spotlight images.
        // Later when we want to unify this behaviour we can turn on this flag and undo this change.
        // isUnifiedAppCampaignWindowsStore={flow === FLOWS.WINDOWS}
        imageAssetsOptional={flow === FLOWS.WINDOWS}
      />
    </div>), [i18n, permissions, postAssetEventLog, suggestionPoller, previewImagesSharedState, assetGroup, perfMarker, flow]);

  const transparentLogoComponent = useMemo(() => (
    <div>
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        imagesContaineRef={transparentLogoContainerRef}
        portraitContainerRef={portraitContainerRef}
        isLogoEnabled={false}
        isBoostPortraitEnabled={false}
        previewImagesSharedState={previewTransparentLogoSharedState}
        setPreviewImagesSharedState={setPreviewTransparentLogoSharedState}
        updatePortalContainerState={setTransparentLogoExperiencePortalContainerState}
        validationProps={{ data: assetGroup, initialValue: assetGroup }}
        wizardPerfMarker={perfMarker}
        customMaxImageCount={1}
        imageListPropName="TransparentLogo"
        imageAssetsOptional
        isStoreAppTransparentLogo
        isCombinedMsanAndMmaAspectRatios={false}
        isCopilotRecommendations={false}
      />
    </div>), [i18n, permissions, perfMarker, assetGroup, previewTransparentLogoSharedState]);

  const validatedImageSPOTLIGHTExperienceWrapper = useMemo(() => (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      imagesContaineRef={ImageSPOTLIGHTContainerRef}
      portraitContainerRef={portraitContainerRef}
      isLogoEnabled={false}
      isBoostPortraitEnabled={false}
      previewImagesSharedState={previewImageSPOTLIGHTSharedState}
      setPreviewImagesSharedState={setPreviewImageSPOTLIGHTSharedState}
      updatePortalContainerState={setImageSPOTLIGHTExperiencePortalContainerState}
      setImageExperienceAddNewImageCallback={setImageSpotlightExperienceAddNewImageCallback}
      validationProps={{ data: assetGroup, initialValue: assetGroup }}
      wizardPerfMarker={perfMarker}
      customMaxImageCount={MaxImageCount}
      imageListPropName="ImageSPOTLIGHT"
      imageAssetsOptional
      isStoreAppSpotLight
      isCombinedMsanAndMmaAspectRatios={false}
      isCopilotRecommendations={false}
    />
  ), [i18n, permissions, previewImageSPOTLIGHTSharedState, assetGroup, perfMarker]);

  const validatedImagePRONGExperienceWrapper = useMemo(() => (
    <ValidatedImageExperienceWrapper
      i18n={i18n}
      permissions={permissions}
      imagesContaineRef={ImagePRONGContainerRef}
      portraitContainerRef={portraitContainerRef}
      isLogoEnabled={false}
      isBoostPortraitEnabled={false}
      previewImagesSharedState={previewImagePRONGSharedState}
      setPreviewImagesSharedState={setPreviewImagePRONGSharedState}
      updatePortalContainerState={setImagePRONGExperiencePortalContainerState}
      setImageExperienceAddNewImageCallback={setProngImageExperienceAddNewImageCallback}
      validationProps={{ data: assetGroup, initialValue: assetGroup }}
      isDisplayPlus
      wizardPerfMarker={perfMarker}
      customMaxImageCount={MaxImageCount}
      imageListPropName="ImagePRONG"
      imageAssetsOptional
      isStoreAppProng
      isCombinedMsanAndMmaAspectRatios={false}
      isCopilotRecommendations={false}
    />
  ), [i18n, permissions, previewImagePRONGSharedState, assetGroup, perfMarker]);

  const adPreview = useMemo(() => {
    const previewProps = {
      i18n,
      imageList: Images && Images.ImageList,
      updateImageList: onUpdateImageList,
      shortHeadline: (Headlines && Headlines.Headline0 !== '' && Headlines.Headline0) || undefined,
      longHeadline: (LongHeadlines && LongHeadlines.LongHeadline0 !== '' && LongHeadlines.LongHeadline0) || undefined,
      description: (Descriptions && Descriptions.Description0 !== '' && Descriptions.Description0) || undefined,
      businessName: AppSetting && AppSetting.AppTitle,
      callToAction: CallToAction,
      videos: (videos.length > selectedVideoId && selectedVideoId !== null) ? [videos[selectedVideoId]] : [],
      displayUrl: finalUrlFromApp,
      url: finalUrlFromApp,
      previewImagesSharedState,
      setPreviewImagesSharedState,
      adPreviewConfig,
      autoHeightCarousel: true,
      customSurfaceKeys: unifiedAppAdsSurfaces,
      appLogo,
    };
    return (
      <PerfMaxResponsiveAdPreviewUAPL
        {...previewProps}
        logoPreviewEnabled
      />
    );
  }, [i18n, Images, videos, appLogo, AppSetting, selectedVideoId, finalUrlFromApp, onUpdateImageList, Headlines, LongHeadlines, Descriptions, CallToAction, previewImagesSharedState]);

  const windowsAppAdsPreivew = useMemo(() => (
    <AppAdsPreview
      i18n={i18n}
      imageList={union(ImageSPOTLIGHT, ImagePRONG, TransparentLogo)}
      updateImageList={noop}
      shortHeadline={Headlines && Headlines.Headline0}
      businessName={AppSetting && AppSetting.AppTitle}
      previewImagesSharedState={previewImageSPOTLIGHTSharedState}
      setPreviewImagesSharedState={setPreviewImageSPOTLIGHTSharedState}
      hideHeaderButtons
      isAppCampaignPreview
      showStart
    />
  ), [i18n, Headlines, ImageSPOTLIGHT, ImagePRONG, TransparentLogo, AppSetting, previewImageSPOTLIGHTSharedState]);

  useEffect(() => {
    if (AppSetting && AppSetting.IconUrl) {
      getGenericImageObjectAsync(AppSetting.IconUrl).then((iconImage) => {
        setAppLogo({
          ...iconImage,
          CropSettings: {},
        });
      });
    }
  }, [AppSetting]);

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors: saveFailed }}>
          <div className={classNames(getStyles('container'), 'asset-group-controls-with-name')}>
            <div className={classNames(getStyles('formContainer'), 'asset-group-controls-container')}>
              <StepHeader
                title={_TL_('Asset group')}
                description={_TL_('This campaign consists of asset groups, which contain the information and media you want to include in your ads.')}
              />
              <UnmappedFormErrors className="other-server-errors" />
              <SystemErrors errors={systemErrors} />
              <div>
                <AssetGroupValidationGroup
                  data={data && {
                    ...data.assetGroup,
                    FinalUrlForRecommendation: finalUrlFromApp,
                  }}
                  onDataChange={onDataChange}
                  isValidating={isMounted.current}
                  i18n={mergedI18n}
                >
                  <CopilotRepositoryProvider
                    campaignId={-1}
                    finalUrl={finalUrlFromApp}
                    locale={i18n.getLocale()}
                    perfMarker={perfMarker}
                    recommendationType="Store" // todo recommendation type
                    serverContext={serverContext}
                    enableLogoAndCTA={false}
                  >
                    <AssetGroupNameSection
                      className="asset-group-name-section"
                      flow={flow}
                      isValidating={isMounted.current}
                      i18n={mergedI18n}
                      onDataChange={onDataChange}
                      campaign={data && data.campaign}
                    />
                    {copilotBanner}
                    <AssetsSection
                      imageExperiencePortalContainerState={imageExperiencePortalContainerState}
                      imageExperienceAddNewImageCallback={imageExperienceAddNewImageCallback}
                      prongImageExperienceAddNewImageCallback={prongImageExperienceAddNewImageCallback}
                      imageSpotlightExperienceAddNewImageCallback={imageSpotlightExperienceAddNewImageCallback}
                      imageExpereienceProngPortalContainerState={ImagePRONGExperiencePortalContainerState}
                      imageExperienceSpotLightPortalContainerState={ImageSPOTLIGHTExperiencePortalContainerState}
                      setAddNewDescriptionCallback={setAddNewDescriptionCallback}
                      setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
                      transparentLogoExperiencePortalContainerState={transparentLogoExperiencePortalContainerState}
                      imagesContainerRef={imagesContainerRef}
                      imageSpotlightContainerRef={ImageSPOTLIGHTContainerRef}
                      imageProngContainerRef={ImagePRONGContainerRef}
                      transparentLogoContainerRef={transparentLogoContainerRef}
                      logosContainerRef={logosContainerRef}
                      mergedI18n={mergedI18n}
                      perfMarker={perfMarker}
                      data={data && data.assetGroup}
                      assetPanels={assetPanels}
                      setSelectedVideoId={setSelectedVideoId}
                      selectedVideoId={selectedVideoId}
                    />
                    {flow !== FLOWS.WINDOWS && imageExperienceComponents}
                    {flow === FLOWS.WINDOWS && transparentLogoComponent}
                    {flow === FLOWS.WINDOWS && validatedImageSPOTLIGHTExperienceWrapper}
                    {flow === FLOWS.WINDOWS && validatedImagePRONGExperienceWrapper}
                  </CopilotRepositoryProvider>
                </AssetGroupValidationGroup>
              </div>
            </div>
            {
              flow === FLOWS.WINDOWS ?
                <div className={classNames(getStyles('windowsAdPreview'), 'image-experience-content')}>
                  {windowsAppAdsPreivew}
                </div>
                :
                <div className={classNames(getStyles('adPreview'), 'image-experience-content')}>
                  {adPreview}
                </div>
            }
          </div>
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

AssetsStepFormInternal.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.number,
      TimeZone: PropTypes.string,
    }),
    assetGroup: PropTypes.shape({
      FinalUrls: PropTypes.arrayOf(PropTypes.string),
      Images: PropTypes.shape({
        ImageList: PropTypes.arrayOf(PropTypes.string),
        ImageAssets: PropTypes.shape({}),
      }),
      CallToAction: PropTypes.shape({
        cta: PropTypes.string,
      }),
      FinalUrlForRecommendation: PropTypes.string,
      BusinessName: PropTypes.string,
      Headlines: PropTypes.shape({}),
      LongHeadlines: PropTypes.shape({}),
      Descriptions: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.string),
    systemErrors: PropTypes.arrayOf(PropTypes.string),
  }),
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
  assetPanels: PropTypes.shape({}).isRequired,
};

AssetsStepFormInternal.defaultProps = {
  error: {},
};

export const AssetsStepForm = withBannerService(withScope(AssetsStepFormInternal));
